import React from "react";
import SearchSortTable from "components/Table";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import FiltersBar from "./features/Filters/Filters";
import EmptyState from "./components/EmptyState";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import ContextMenu from "./components/TableMenus/CellMenu";
import HeaderMenu from "./components/TableMenus/HeaderMenu";
import Tooltip from "components/Tooltip";
import {
  filterInventoryStore,
  filterMEStore,
} from "./features/Filters/FiltersStore";
import {
  dynamicTableStore,
  TableMode,
  TableModeType,
} from "./DynamicTableStore";
import useDynamicTable from "./useDynamicTable";
import { useParams } from "react-router";
import { buildingStore } from "store/Building/BuildingStore";

const DynamicTable = (props: {
  mode: TableModeType;
  getColumns: () => ColumnDef<unknown, unknown>[];
  title?: string;
  onClickRow?: (row: Row<unknown>) => void;
  onKeyPressed?: (key: string, type: string) => void;
  onMouseDragged?: (row: Row<unknown>, direction: string) => void;
  getData?: (start: number, size: number, update?: boolean) => Promise<unknown>;
  onHoverRow?: (row: Row<unknown> | null) => void;
  onRowSelect?: (e: string[]) => void;
  disableUserInteraction?: boolean;
  selectedRows?: string[];
  selectAllChecked?: boolean;
  currentRowID?: string;
  columnStyles?: string;
}) => {
  const { updateColumnsVisibilitySavedState, retrieveOrderSavedState } =
    useDynamicTable();
  const { id } = useParams();
  const isME = dynamicTableStore.mode === TableMode.MappingEditor;
  const filtersStore = isME ? filterMEStore : filterInventoryStore;

  React.useEffect(() => {
    initialCall();

    return () => dynamicTableStore.resetTableData();
  }, []);

  async function initialCall() {
    await Promise.all([
      buildingStore.setBuildingID(id),
      dynamicTableStore.setMode(props.mode),
      dynamicTableStore.fetchColumns(),
      dynamicTableStore.getProperties(),
    ]);
    updateColumnsVisibilitySavedState();
    retrieveOrderSavedState();
  }

  const noResultsForFiltering = () =>
    filtersStore.selectedFilterByItems.rules.length &&
    dynamicTableStore.data.items.length === 0 &&
    !dynamicTableStore.dataFetchLoading &&
    !dynamicTableStore.errorFetchingObjects;

  const showTable = !dynamicTableStore.errorFetchingObjects;

  {
    /* If TableMode is MappingEditor, it includes 0 to separate the selection column. */
  }
  const deviderIndexes = [
    ...(isME ? [0] : []),
    ...(filtersStore.validGroupByRules.length
      ? [filtersStore.validGroupByRules.length + (isME ? 1 : 0)]
      : []),
  ];

  if (!dynamicTableStore.visibleColumnProperties?.length) {
    console.log("No visible column properties");
    return <></>;
  }

  return (
    <>
      <div
        className={clsx("flex w-full px-1 border-b border-gray-300 bg-white", {
          "pt-2 pb-1 px-4": props.title,
        })}
      >
        {props.title && (
          <div className="text-xl font-semibold flex items-center">
            {props.title}
            <Tooltip content={props.title} className="text-gray-500" />
          </div>
        )}

        <FiltersBar isInventory={!isME} filterStore={filtersStore} />
      </div>

      {dynamicTableStore.errorFetchingObjects && <EmptyState />}

      {noResultsForFiltering() ? (
        <EmptyState />
      ) : (
        showTable && (
          <SearchSortTable
            getColumns={props.getColumns}
            data={dynamicTableStore.data.items.map((item) => ({
              ...item.values,
              id: item.id,
              product_match: item.product_match,
              ifc_global_id: item.ifc_global_id,
              object_count: item.object_count,
              calculation_count: item.calculation_count,
            }))}
            hasGroupHeader
            enableColumnResizing
            totalDBRowCount={dynamicTableStore.data.count}
            dataFetchLoading={dynamicTableStore.dataFetchLoading}
            fetchData={props.getData}
            enableTooltip
            scrollDivClassName="max-h-full w-full bg-white"
            customColumnStyle={clsx(
              "!py-0 !font-medium h-[40px] border-r-gray-200 border-r text-gray-900",
              filtersStore.hasGrouping() ? "cursor-auto" : "cursor-pointer",
              props.columnStyles
            )}
            headerDivStyle="!py-1.5 font-semibold"
            headerColumnStyle="!py-0 border-r-gray-200"
            tableStyle="border-b"
            groupHeaderClassName="!pt-0 !pb-0 !px-0"
            enableRowSelection
            onRowSelect={props.onRowSelect}
            noFooterBar
            hasHoverStyle
            onRowClick={props.onClickRow}
            selectedRows={props.selectedRows}
            selectAllChecked={props.selectAllChecked}
            hiddenColumns={dynamicTableStore.columnsVisibilityState}
            columnsOrder={dynamicTableStore.tableColumnsOrder}
            currentRowID={props.currentRowID}
            disableUserInteraction={props.disableUserInteraction}
            onHoveredRow={props.onHoverRow}
            deviderIndexes={deviderIndexes}
            customContextMenuComponent={(info, onClose) => (
              <ContextMenu onClose={onClose} info={info} />
            )}
            headerMenuComponent={(
              column: Column<unknown, unknown>,
              closeHeaderMenu: () => void
            ) => <HeaderMenu column={column} closeMenu={closeHeaderMenu} />}
            handleKeyPressed={props.onKeyPressed}
            handleMouseDragged={props.onMouseDragged}
            hasDragging
            zebraStriped={dynamicTableStore.mode === TableMode.Inventory}
          />
        )
      )}
    </>
  );
};

export default observer(DynamicTable);
